import dynamic from 'next/dynamic';
import HeroBanner from '@quicktools/shared-components/components/landingPages/HeroBanner/HeroBanner';
import {
  COMPRESS_IMAGE_TOOL,
  PNG_TO_JPG_TOOL,
  RESIZE_IMAGE_TOOL,
  JPG_TO_PNG_TOOL,
  ToolCategory,
} from '@quicktools/shared-components/data/tools';
import HeroFileUploader from '@quicktools/shared-components/components/landingPages/HeroFileUploader';
import { FormatIconType } from '@quicktools/shared-components/components/core/FormatIcon/types';
import useSnackbarStore from '@quicktools/shared-components/hooks/snackbar/useSnackbarStore';
import SnackbarList from '@quicktools/shared-components/components/core/SnackbarList/SnackbarList';
import Loader from '@quicktools/shared-components/components/core/ConvertItem/Loader';
import useUploadArgs from '@quicktools/shared-components/hooks/useUploadArgs';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { WithLanding } from '@quicktools/shared-components';
import getBreadcrumbSublinks from '@quicktools/shared-components/utils/breadcrumbSubLinks';

import validateFn from '../../utils/isJpegFile';
import convertFn from '../../utils/convert';
import { appConfig, canonicalPath } from '../../configs';
import analytics from '../../services/analytics';

import FaqSection from './FaqSection';
import HowToSection from './HowToSection';
import ImageSectionWhy from './ImageSectionWhy';
import ImageSection from './ImageWithTextWidget';

const SIMILAR_TOOLS = [RESIZE_IMAGE_TOOL, COMPRESS_IMAGE_TOOL, PNG_TO_JPG_TOOL];

const List = dynamic(() => import('@quicktools/shared-components/components/core/Converter'), {
  loading: () => <Loader isSingle>Processing...</Loader>,
});

const { accept, multiple } = appConfig;

function Landing() {
  const { t } = useTranslation();
  const snackItems = useSnackbarStore((state) => state.snackItems);
  const snackbarClose = useSnackbarStore((state) => state.snackbarClose);
  const [onUpload, inputFiles, uploadType] = useUploadArgs();

  useEffect(() => {
    analytics.pageOpen();
  }, []);

  const uploader = useMemo(
    () => (
      <HeroFileUploader
        accept={appConfig.accept}
        multiple={appConfig.multiple}
        onUpload={onUpload}
        paragraph={
          <Trans i18nKey="landing.heroBanner.dragAreaParagraph" components={{ span: <span /> }} />
        }
        btnLabel={t('landing.heroBanner.cta')}
      />
    ),
    [],
  );

  const footer = inputFiles.length ? (
    <List
      toolName={t('seo.toolName')}
      showShareModal={true}
      accept={accept}
      convertFn={convertFn}
      finalExtension={FormatIconType.PNG}
      inputFiles={inputFiles}
      multiple={multiple}
      originalExtension={FormatIconType.JPG}
      uploader={uploader}
      uploadType={uploadType}
      validateFn={validateFn}
      analytics={analytics}
    />
  ) : (
    uploader
  );

  return (
    <WithLanding
      analytics={analytics}
      currentTool={JPG_TO_PNG_TOOL}
      breadcrumbItems={getBreadcrumbSublinks(canonicalPath)}
      similarTools={SIMILAR_TOOLS}
      similarToolsParagraph={t('landing.similarTools.paragraph')}
      toolCategory={ToolCategory.Convert}
    >
      <SnackbarList snackItems={snackItems} onClose={snackbarClose} />
      <HeroBanner
        heading={t('landing.heroBanner.title')}
        content={t('landing.heroBanner.subTitle')}
        imgSrc={appConfig.heroBannerImgUrl}
        imgAlt={appConfig.heroBannerImgAlt}
        partialBg
        footer={footer}
      />
      <ImageSection />
      <HowToSection onUpload={onUpload} />
      <ImageSectionWhy />
      <FaqSection />
    </WithLanding>
  );
}

export default Landing;
